import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTags } from 'react-tag-autocomplete';
import CurrencyTagsInput from '../../../components/form/currency-tags-input.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import { ACCOUNT_TYPE_OPTIONS } from '../dto/system-trading-accounts.dto.js';

function SystemTradingAccountsPageView({
  rows,
  platformOptions,
  currencyOptions,
  isLoading,
  isSaving,
  error,
  success,
  detailsRow,
  serverLabelById,
  isCreateOpen,
  editingTargetId,
  deleteTargetId,
  form,
  formErrors,
  modalError,
  leverageInput,
  setLeverageInput,
  closeModals,
  openCreate,
  openEdit,
  openDetails,
  requestDelete,
  onFormChange,
  filteredTradingServers,
  leverageTags,
  addLeverageTagValue,
  onLeverageTagDelete,
  onLeverageTagAdd,
  saveForm,
  confirmDelete,
}) {
  const { t } = useTranslation();
  const capabilities = platformOptions.find(option => option.value === form.platform)?.capabilities ?? form.capabilities ?? {};
  const accountTypes = capabilities.allowedAccountTypes ?? ACCOUNT_TYPE_OPTIONS;
  const allowedCurrencies = capabilities.allowedCurrencies ?? [];
  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('tradingAccounts.savingInfo') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('tradingAccounts.title')}</h2>
          <p>{t('tradingAccounts.subtitle')}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={openCreate}
          disabled={isLoading || isSaving || platformOptions.length === 0}
        >
          {t('tradingAccounts.addTradingAccount')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th style={{ width: '110px' }}>{t('tradingAccounts.colPlatform')}</th>
                    <th style={{ width: '180px' }}>{t('tradingAccounts.colTradingServer')}</th>
                    <th>{t('tradingAccounts.colName')}</th>
                    <th style={{ width: '180px' }}>{t('tradingAccounts.colAccountGroup')}</th>
                    <th style={{ width: '100px' }}>{t('tradingAccounts.colType')}</th>
                    <th style={{ width: '140px' }}>{t('tradingAccounts.colLeverages')}</th>
                    <th style={{ width: '160px' }}>{t('tradingAccounts.colCurrencies')}</th>
                    <th style={{ width: '140px' }}>{t('tradingAccounts.colMinimumDeposit')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('tradingAccounts.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">{t('tradingAccounts.noTradingAccountsFound')}</td>
                    </tr>
                  ) : null}
                  {rows.map((row) => (
                    <tr key={row.id} className="app-clickable-row" onClick={() => openDetails(row.id)}>
                      <td>{row.platform?.toUpperCase() || '-'}</td>
                      <td>{serverLabelById.get(row.tradingServerId) || row.tradingServerId || '-'}</td>
                      <td>{row.name || '-'}</td>
                      <td>{row.group || '-'}</td>
                      <td>{row.type || '-'}</td>
                      <td>{row.leverages.join(', ') || '-'}</td>
                      <td>{row.currencies.join(', ') || '-'}</td>
                      <td>{row.minimumDeposit === null ? '-' : row.minimumDeposit}</td>
                      <td className="text-end text-nowrap">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            title="Edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row);
                            }}
                            disabled={isSaving}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            title="Delete"
                            onClick={(event) => {
                              event.stopPropagation();
                              requestDelete(row.id);
                            }}
                            disabled={isSaving}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </LoadingContainer>
        </div>
      </div>

      {detailsRow ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('tradingAccounts.detailsTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldPlatform')}:</strong><div>{detailsRow.platform?.toUpperCase() || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldTradingServer')}:</strong><div>{serverLabelById.get(detailsRow.tradingServerId) || detailsRow.tradingServerId || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldName')}:</strong><div>{detailsRow.name || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldDescription')}:</strong><div>{detailsRow.description || '-'}</div></div>
                  <div className="mb-3">
                    <strong>{t('tradingAccounts.fieldPlatformUrl')}:</strong>
                    <div>
                      {detailsRow.platformUrl ? (
                        <a href={detailsRow.platformUrl} target="_blank" rel="noreferrer">
                          {detailsRow.platformUrl}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldType')}:</strong><div>{detailsRow.type || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldLeverages')}:</strong><div>{detailsRow.leverages.join(', ') || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldCurrencies')}:</strong><div>{detailsRow.currencies.join(', ') || '-'}</div></div>
                  <div className="mb-3"><strong>{t('tradingAccounts.fieldAccountGroup')}:</strong><div>{detailsRow.group || '-'}</div></div>
                  <div><strong>{t('tradingAccounts.fieldMinimumDeposit')}:</strong><div>{detailsRow.minimumDeposit === null ? '-' : detailsRow.minimumDeposit}</div></div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals}>{t('common.close')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {isCreateOpen || editingTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingTargetId ? t('tradingAccounts.editTitle') : t('tradingAccounts.addTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <form onSubmit={saveForm}>
                  <div className="modal-body">
                    {modalError ? <div className="alert alert-danger py-2">{modalError}</div> : null}
                    <div className="mb-3">
                      <label className="form-label">{t('tradingAccounts.nameLabel')}</label>
                      <input
                        className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                        value={form.name}
                        onChange={(event) => onFormChange('name', event.target.value)}
                        required
                      />
                      {formErrors.name ? <div className="invalid-feedback">{formErrors.name}</div> : null}
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('tradingAccounts.platformLabel')}</label>
                        <select
                          className={`form-select ${formErrors.platform ? 'is-invalid' : ''}`}
                          value={form.platform}
                          onChange={(event) => onFormChange('platform', event.target.value)}
                        >
                          <option value="">{t('tradingAccounts.selectPlatform')}</option>
                          {platformOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {formErrors.platform ? <div className="invalid-feedback">{formErrors.platform}</div> : null}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('tradingAccounts.tradingServerLabel')}</label>
                        <select
                          className={`form-select ${formErrors.tradingServerId ? 'is-invalid' : ''}`}
                          value={form.tradingServerId}
                          onChange={(event) => onFormChange('tradingServerId', event.target.value)}
                        >
                          <option value="">{t('tradingAccounts.selectTradingServer')}</option>
                          {filteredTradingServers.map((server) => (
                            <option key={server.id} value={server.id}>{server.name || server.id}</option>
                          ))}
                        </select>
                        {formErrors.tradingServerId ? <div className="invalid-feedback">{formErrors.tradingServerId}</div> : null}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('tradingAccounts.typeLabel')}</label>
                        <select
                          className={`form-select ${formErrors.type ? 'is-invalid' : ''}`}
                          value={form.type}
                          disabled={accountTypes.length === 1}
                          onChange={(event) => onFormChange('type', event.target.value)}
                        >
                          {accountTypes.map((item) => (
                            <option key={item} value={item}>{getEnumOptionLabel(t, 'accountType', item)}</option>
                          ))}
                        </select>
                        {formErrors.type ? <div className="invalid-feedback">{formErrors.type}</div> : null}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('tradingAccounts.accountGroupLabel')}</label>
                        <input
                          className={`form-control ${formErrors.group ? 'is-invalid' : ''}`}
                          value={form.group}
                          onChange={(event) => onFormChange('group', event.target.value)}
                        />
                        {formErrors.group ? <div className="invalid-feedback">{formErrors.group}</div> : null}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('tradingAccounts.descriptionLabel')}</label>
                      <input
                        className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                        value={form.description}
                        onChange={(event) => onFormChange('description', event.target.value)}
                        required
                      />
                      {formErrors.description ? <div className="invalid-feedback">{formErrors.description}</div> : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('tradingAccounts.platformUrlLabel')}</label>
                      <input
                        type="url"
                        className={`form-control ${formErrors.platformUrl ? 'is-invalid' : ''}`}
                        value={form.platformUrl}
                        onChange={(event) => onFormChange('platformUrl', event.target.value)}
                      />
                      {formErrors.platformUrl ? <div className="invalid-feedback">{formErrors.platformUrl}</div> : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('tradingAccounts.leveragesLabel')}</label>
                      <div className="d-flex gap-2 mb-2">
                        <input
                          type="number"
                          min={capabilities.minLeverage ?? 1}
                          max={capabilities.maxLeverage ?? undefined}
                          step={1}
                          className="form-control form-control-sm"
                          style={{ maxWidth: '140px' }}
                          placeholder="e.g. 100"
                          value={leverageInput}
                          onChange={(event) => setLeverageInput(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              addLeverageTagValue(leverageInput);
                              setLeverageInput('');
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            addLeverageTagValue(leverageInput);
                            setLeverageInput('');
                          }}
                        >
                          Add
                        </button>
                      </div>
                      <ReactTags
                        selected={leverageTags}
                        suggestions={[]}
                        allowNew={false}
                        onDelete={onLeverageTagDelete}
                        onAdd={onLeverageTagAdd}
                        placeholderText={t('tradingAccounts.leveragesPlaceholder')}
                      />
                      {formErrors.leverages ? <div className="text-danger small mt-1">{formErrors.leverages}</div> : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('tradingAccounts.currenciesLabel')}</label>
                      {allowedCurrencies.length === 1 ? <div className="form-control bg-light">{allowedCurrencies[0]}</div> : <CurrencyTagsInput
                        value={form.currencies}
                        options={currencyOptions.filter(option => !allowedCurrencies.length || allowedCurrencies.includes(option.value))}
                        onChange={(currencies) => onFormChange('currencies', currencies)}
                        placeholderText={t('tradingAccounts.currenciesPlaceholder')}
                        listMaxHeight={200}
                      />}
                      {formErrors.currencies ? <div className="text-danger small mt-1">{formErrors.currencies}</div> : null}
                    </div>

                    {capabilities.supportsStopOut ? <div className="mb-3">
                      <label className="form-label" htmlFor="platform-stop-out">Stop out (%)</label>
                      <input id="platform-stop-out" type="number" min="0.01" max="100" step="0.01" value={form.stopOutPercent ?? '50.00'}
                        className={`form-control ${formErrors.stopOutPercent ? 'is-invalid' : ''}`}
                        onChange={(event) => onFormChange('stopOutPercent', event.target.value)} required />
                      {formErrors.stopOutPercent ? <div className="invalid-feedback">{formErrors.stopOutPercent}</div> : null}
                    </div> : null}
                    <div>
                      <label className="form-label">{t('tradingAccounts.minimumDepositLabel')}</label>
                      <input
                        type="number"
                        min="0"
                        className={`form-control ${formErrors.minimumDeposit ? 'is-invalid' : ''}`}
                        value={form.minimumDeposit}
                        onChange={(event) => onFormChange('minimumDeposit', event.target.value)}
                        placeholder="Optional"
                      />
                      {formErrors.minimumDeposit ? <div className="invalid-feedback">{formErrors.minimumDeposit}</div> : null}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>{editingTargetId ? t('common.save') : t('common.create')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {deleteTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('tradingAccounts.deleteTitle')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  {t('tradingAccounts.deleteConfirm')}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isSaving}>{t('common.delete')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </div>
  );
}

export default SystemTradingAccountsPageView;
